import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Users,
  CalendarCheck,
  BrainCircuit,
  Clock,
  Sparkles,
  TrendingUp,
  Zap,
  Timer,
  Bot,
  UserCheck,
  AlertTriangle,
  Home,
  Eye,
  ArrowUpRight,
  Activity,
  DollarSign,
  Building2,
  Flame,
  BarChart3,
  Loader2,
  ExternalLink,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import Cookies from "js-cookie";
import { formatAiCredits } from "@/lib/formatAiCredits";
import { getClientAuthHeaders, getClientAuthToken } from "@/lib/apiAuth";
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
} from "recharts";

// ─── MOCK DATA ─────────────────────────────────────────────────────────────────
// Sample dashboard data for demo mode (useLiveData === false).

const MOCK_KPI = {
  totalLeads: 247,
  meetingsBooked: 168,
  autonomyRate: 94,
  hoursSaved: 186,
};

const MOCK_FUNNEL = {
  totalLeads: 247,
  qualificationRate: 74,
  meetingsBooked: 168,
  bookingRate: 68,
};

const MOCK_SPEED = {
  avgResponseTime: "12 sec",
  aiAutonomyRate: 94,
  manualHoursSaved: 186,
};

const MOCK_BUDGET_DISTRIBUTION = [
  { range: "< $500K", count: 42, color: "#6366F1" },
  { range: "$500K–$1M", count: 78, color: "#8B5CF6" },
  { range: "$1M–$3M", count: 91, color: "#D4AF37" },
  { range: "$3M+", count: 36, color: "#F59E0B" },
];

const MOCK_BUYER_TYPE = [
  { name: "Investors", value: 58, color: "#D4AF37" },
  { name: "Owner-Occupiers", value: 42, color: "#00D1FF" },
];

const MOCK_HOT_LEADS = [
  {
    id: "mock-1",
    name: "Sarah Mitchell",
    contact: "+61 412 *** 789",
    status: "Handoff Requested",
    budget: "$2.5M",
    time: "12 min ago",
    platform: "whatsapp",
  },
  {
    id: "mock-2",
    name: "James Huang",
    contact: "+61 433 *** 102",
    status: "Booking Stage",
    budget: "$1.8M",
    time: "34 min ago",
    platform: "telegram",
  },
  {
    id: "mock-3",
    name: "Emma Patel",
    contact: "+61 421 *** 567",
    status: "Handoff Requested",
    budget: "$3.2M",
    time: "1 hr ago",
    platform: "ghl",
  },
  {
    id: "mock-4",
    name: "Michael Torres",
    contact: "+61 409 *** 345",
    status: "Meeting Booked",
    budget: "$950K",
    time: "2 hr ago",
    platform: "whatsapp",
  },
];

const MOCK_TOP_PROPERTIES = [
  { id: 1, name: "Penthouse – 42 Harbour View Rd", enquiries: 34, trend: "+12%" },
  { id: 2, name: "3BR Villa – 18 Coastal Blvd", enquiries: 28, trend: "+8%" },
  { id: 3, name: "Studio Apt – 7 City Walk", enquiries: 21, trend: "+5%" },
  { id: 4, name: "4BR House – 91 Palm St", enquiries: 17, trend: "+3%" },
  { id: 5, name: "2BR Unit – 55 Ocean Dr", enquiries: 14, trend: "+1%" },
];

// Message volume for last 30 days (~2.2–2.4k total). Weekends softer; per-day jitter (no fixed 7-day wave).
const buildMockActivityData = () => {
  const pseudoRandom01 = (seed) => {
    const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
    return x - Math.floor(x);
  };

  return Array.from({ length: 30 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (29 - i));
    const dayOfWeek = d.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    const seed = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
    const r1 = pseudoRandom01(seed);
    const r2 = pseudoRandom01(seed + 1);
    const r3 = pseudoRandom01(seed + 2);

    const base = isWeekend ? 36 + r1 * 18 : 68 + r1 * 32;
    const bump = r2 > 0.82 ? 14 + r3 * 20 : r2 < 0.1 ? -(8 + r3 * 14) : (r2 - 0.5) * 12;
    const messages = Math.round(Math.max(28, Math.min(118, base + bump)));

    return {
      date: d.toLocaleDateString("en-US", { month: "short", day: "2-digit" }),
      messages,
    };
  });
};

const MOCK_ACTIVITY_DATA = buildMockActivityData();

const MOCK_RECENT_ACTIVITY = [
  {
    id: "mock-act-1",
    type: "new_lead",
    text: "New lead: Sarah Mitchell via WhatsApp",
    time: "2 min ago",
  },
  {
    id: "mock-act-2",
    type: "handoff_requested",
    text: "Handoff requested: Emma Patel",
    time: "18 min ago",
  },
  {
    id: "mock-act-3",
    type: "meeting_booked",
    text: "Meeting booked: Michael Torres",
    time: "45 min ago",
  },
  {
    id: "mock-act-4",
    type: "new_lead",
    text: "New lead: James Huang via Telegram",
    time: "1 hr ago",
  },
];

const EMPTY_KPI = {
  totalLeads: 0,
  meetingsBooked: 0,
  autonomyRate: 0,
  hoursSaved: 0,
};

const EMPTY_FUNNEL = {
  totalLeads: 0,
  qualificationRate: 0,
  meetingsBooked: 0,
  bookingRate: 0,
};

const EMPTY_SPEED = {
  avgResponseTime: "N/A",
  aiAutonomyRate: 0,
  manualHoursSaved: 0,
};

// ─── HELPER COMPONENTS ─────────────────────────────────────────────────────────

const GoldKPICard = ({ icon: Icon, label, value, suffix, description }) => (
  <div className="relative overflow-hidden rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/10 via-[#0F1115] to-[#0F1115] p-5 md:p-6 group hover:border-[#D4AF37]/50 transition-all duration-300">
    <div className="absolute top-0 right-0 w-24 h-24 bg-[#D4AF37]/5 rounded-full -translate-y-8 translate-x-8 group-hover:bg-[#D4AF37]/10 transition-all duration-500" />
    <div className="flex items-start justify-between mb-3">
      <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#D4AF37]" />
      </div>
      <ArrowUpRight className="w-4 h-4 text-[#D4AF37]/50 group-hover:text-[#D4AF37] transition-colors" />
    </div>
    <div className="font-heading font-bold text-3xl md:text-4xl text-[#F3F4F6] mb-1">
      {value}
      {suffix && <span className="text-xl md:text-2xl text-[#D4AF37]">{suffix}</span>}
    </div>
    <div className="text-[#9CA3AF] text-sm font-body">{label}</div>
    {description && (
      <div className="text-[#D4AF37]/60 text-xs font-body mt-1">{description}</div>
    )}
  </div>
);

const SectionHeading = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className="w-8 h-8 rounded-lg bg-[#00D1FF]/10 flex items-center justify-center">
      <Icon className="w-4 h-4 text-[#00D1FF]" />
    </div>
    <div>
      <h2 className="font-heading font-bold text-lg text-[#F3F4F6]">{title}</h2>
      {subtitle && <p className="text-[#9CA3AF] text-xs font-body">{subtitle}</p>}
    </div>
  </div>
);

const StatCard = ({ label, value, suffix, icon: Icon, color = "#00D1FF", description }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 hover:border-white/20 transition-all">
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4" style={{ color }} />
      <span className="text-[#9CA3AF] text-xs font-body uppercase tracking-wide">{label}</span>
    </div>
    <div className="font-heading font-bold text-2xl text-[#F3F4F6]">
      {value}
      {suffix && <span className="text-base ml-1" style={{ color }}>{suffix}</span>}
    </div>
    {description && (
      <div className="text-[#9CA3AF] text-xs font-body mt-1">{description}</div>
    )}
  </div>
);

const NotificationItem = ({ type, text, time }) => {
  const isLead = type === "new_lead";
  const isHandoff = type === "handoff_requested";
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-white/[0.03] transition-colors">
      <div
        className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
          isHandoff ? "bg-amber-400" : isLead ? "bg-[#00D1FF]" : "bg-[#D4AF37]"
        }`}
      />
      <div className="flex-1 min-w-0">
        <p className="text-[#F3F4F6] text-sm font-body truncate">{text}</p>
        <p className="text-[#9CA3AF] text-xs font-body mt-0.5">{time}</p>
      </div>
    </div>
  );
};

const CustomTooltipBar = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#1a1d24] border border-white/20 rounded-lg px-3 py-2 shadow-xl">
        <p className="text-[#F3F4F6] text-xs font-body">{label}</p>
        <p className="text-[#D4AF37] text-sm font-heading font-bold">{`${payload[0].value} messages`}</p>
      </div>
    );
  }
  return null;
};

const CustomTooltipPie = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#1a1d24] border border-white/20 rounded-lg px-3 py-2 shadow-xl">
        <p className="text-[#F3F4F6] text-sm font-body">
          {payload[0].name}: <span className="font-bold">{payload[0].value}%</span>
        </p>
      </div>
    );
  }
  return null;
};

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────────

const Dashboard = () => {
  const navigate = useNavigate();
  // Data source: true = Live API, false = mock. Change initial value or call setUseLiveData in code.
  const [useLiveData, setUseLiveData] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [kpi, setKpi] = useState(EMPTY_KPI);
  const [funnel, setFunnel] = useState(EMPTY_FUNNEL);
  const [speed, setSpeed] = useState(EMPTY_SPEED);
  const [activityData, setActivityData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [hotLeads, setHotLeads] = useState([]);
  const [aiCredits, setAiCredits] = useState(null);

  const applyMockDashboard = () => {
    setLoadError(null);
    setKpi({ ...MOCK_KPI });
    setFunnel({ ...MOCK_FUNNEL });
    setSpeed({ ...MOCK_SPEED });
    setActivityData([...MOCK_ACTIVITY_DATA]);
    setRecentActivity([...MOCK_RECENT_ACTIVITY]);
    setHotLeads([...MOCK_HOT_LEADS]);
  };

  const resetLiveDashboard = () => {
    setKpi({ ...EMPTY_KPI });
    setFunnel({ ...EMPTY_FUNNEL });
    setSpeed({ ...EMPTY_SPEED });
    setActivityData([]);
    setRecentActivity([]);
    setHotLeads([]);
  };

  useEffect(() => {
    if (!useLiveData) {
      applyMockDashboard();
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const fetchLiveDashboard = async () => {
      resetLiveDashboard();
      setLoadError(null);

      const token = getClientAuthToken();
    if (!token) {
        setLoadError("Sign in to load live dashboard statistics.");
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const headers = { Authorization: `Bearer ${token}` };

      try {
        const [metricsRes, activityRes, recentRes, funnelRes, hotLeadsRes, aiUsageRes] = await Promise.all([
          axios.get(`${backendUrl}/api/dashboard/metrics`, { headers }).catch(() => null),
          axios.get(`${backendUrl}/api/dashboard/activity`, { headers }).catch(() => null),
          axios.get(`${backendUrl}/api/dashboard/recent-activity`, { headers }).catch(() => null),
          axios.get(`${backendUrl}/api/dashboard/funnel`, { headers }).catch(() => null),
          axios.get(`${backendUrl}/api/dashboard/hot-leads`, { headers }).catch(() => null),
          axios.get(`${backendUrl}/api/dashboard/ai-usage/summary`, { headers }).catch(() => null),
        ]);

        if (cancelled) return;

        let anySuccess = false;

        if (metricsRes?.data?.status === "success") {
          anySuccess = true;
          const d = metricsRes.data.data;
          setKpi({
            totalLeads: d.total_leads ?? 0,
            meetingsBooked: d.meetings_booked ?? 0,
            autonomyRate: d.ai_autonomy_rate ?? 0,
            hoursSaved: d.hours_saved ?? 0,
          });
          const avgTime = d.avg_response_time ?? 0;
          setSpeed({
            avgResponseTime: avgTime > 0 ? `${avgTime} sec` : "N/A",
            aiAutonomyRate: d.ai_autonomy_rate ?? 0,
            manualHoursSaved: d.hours_saved ?? 0,
          });
        }

        if (activityRes?.data?.status === "success" && Array.isArray(activityRes.data.data)) {
          anySuccess = true;
          setActivityData(activityRes.data.data);
        }

        if (recentRes?.data?.status === "success" && Array.isArray(recentRes.data.data)) {
          anySuccess = true;
          setRecentActivity(recentRes.data.data);
        }

        if (funnelRes?.data?.status === "success" && funnelRes.data.data) {
          anySuccess = true;
          const f = funnelRes.data.data;
          setFunnel({
            totalLeads: f.total_leads ?? 0,
            qualificationRate: f.qualification_rate ?? 0,
            meetingsBooked: f.meetings_booked ?? 0,
            bookingRate: f.booking_rate ?? 0,
          });
        }

        if (hotLeadsRes?.data?.status === "success" && Array.isArray(hotLeadsRes.data.data)) {
          anySuccess = true;
          setHotLeads(hotLeadsRes.data.data);
        }

        if (aiUsageRes?.data) {
          setAiCredits(aiUsageRes.data);
        }

        if (!anySuccess) {
          setLoadError("Could not load dashboard statistics. Please try again.");
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load dashboard data:", err);
          setLoadError("Could not load dashboard statistics. Please try again.");
        }
      }
    };

    setIsLoading(true);
    fetchLiveDashboard().finally(() => {
      if (!cancelled) setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [useLiveData]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0F1115] flex">
        <Sidebar />
        <div className="flex-1 md:ml-64 flex items-center justify-center px-6">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-[#00D1FF] animate-spin mx-auto mb-4" />
            <p className="text-[#9CA3AF] font-body">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1115] flex" data-testid="dashboard-stats-page">
      <Sidebar />

      <div className="flex-1 md:ml-64">
        <main className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
          {/* ── Page Header ─────────────────────────────────────── */}
          <div className="mb-8">
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6] mb-1">
              Dashboard
            </h1>
            <p className="text-[#9CA3AF] font-body text-sm">
              Performance overview &amp; lead analytics
            </p>
            {useLiveData && loadError && (
              <p className="mt-2 text-amber-400/90 text-xs font-body">{loadError}</p>
            )}
          </div>

          {aiCredits && (
            <div
              className="mb-8 rounded-2xl border border-white/10 bg-white/[0.02] p-5 md:p-6"
              data-testid="dashboard-ai-credits"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#00D1FF]/10 border border-[#00D1FF]/20">
                    <Sparkles className="h-5 w-5 text-[#00D1FF]" />
                  </div>
                  <div>
                    <p className="text-xs font-mono uppercase tracking-wider text-[#9CA3AF]">
                      AI Credits this period
                    </p>
                    <p className="text-2xl font-heading font-bold text-[#F3F4F6] mt-1">
                      {formatAiCredits(aiCredits.credits_used)} / {formatAiCredits(aiCredits.credits_included)}
                    </p>
                    <p className="text-sm text-[#9CA3AF] font-body mt-1">
                      Tier {aiCredits.tier} · {aiCredits.usage_percent}% used
                      {aiCredits.hard_blocked ? " · quota reached" : ""}
                    </p>
                  </div>
                </div>
                <div className="w-full sm:w-48 h-2 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${
                      aiCredits.usage_percent >= 100
                        ? "bg-red-400"
                        : aiCredits.usage_percent >= 80
                          ? "bg-amber-400"
                          : "bg-[#00D1FF]"
                    }`}
                    style={{ width: `${Math.min(aiCredits.usage_percent, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ── 1. Gold KPI Row ─────────────────────────────────── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <GoldKPICard
              icon={Users}
              label="Total Leads"
              value={kpi.totalLeads}
              description="WhatsApp + Telegram"
            />
            <GoldKPICard
              icon={CalendarCheck}
              label="Meetings Booked"
              value={kpi.meetingsBooked}
              description="Calendar events"
            />
            <GoldKPICard
              icon={BrainCircuit}
              label="AI Autonomy"
              value={kpi.autonomyRate}
              suffix="%"
              description="Fully AI-handled"
            />
            <GoldKPICard
              icon={Clock}
              label="Hours Saved"
              value={kpi.hoursSaved}
              suffix="h"
              description="vs. manual follow-up"
            />
          </div>

          {/* ── 2. Activity Chart + Notifications ───────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Activity Chart */}
            <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-white/[0.02] p-5 md:p-6">
              <SectionHeading
                icon={Activity}
                title="Message Activity"
                subtitle="Messages processed per day — last 30 days"
              />
              <div className="h-[280px] mt-4">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={activityData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="goldGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: "#9CA3AF", fontSize: 11 }}
                      axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fill: "#9CA3AF", fontSize: 11 }}
                      axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip content={<CustomTooltipBar />} />
                    <Area
                      type="monotone"
                      dataKey="messages"
                      stroke="#D4AF37"
                      strokeWidth={2}
                      fill="url(#goldGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Recent Notifications Feed */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 md:p-6 flex flex-col">
              <SectionHeading
                icon={Zap}
                title="Recent Activity"
                subtitle="Live event feed"
              />
              <div className="flex-1 overflow-y-auto max-h-[320px] mt-2 space-y-1 scrollbar-thin scrollbar-thumb-white/10">
                {recentActivity.length > 0 ? (
                  recentActivity.map((n) => (
                    <NotificationItem key={n.id} type={n.type} text={n.text} time={n.time} />
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center h-full py-8">
                    <Zap className="w-8 h-8 text-[#9CA3AF]/30 mb-2" />
                    <p className="text-[#9CA3AF]/60 text-sm font-body text-center">No recent activity yet</p>
                    <p className="text-[#9CA3AF]/40 text-xs font-body text-center mt-1">Events will appear here as leads interact with your Digital Brain</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── 3. Conversion Funnel ────────────────────────────── */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 md:p-6 mb-8">
            <SectionHeading
              icon={TrendingUp}
              title="Conversion Funnel"
              subtitle="The revenue funnel — from cold traffic to booked meetings"
            />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={Users}
                label="Total Leads"
                value={funnel.totalLeads}
                color="#00D1FF"
                description="Unique contacts"
              />
              <StatCard
                icon={UserCheck}
                label="Qualification Rate"
                value={funnel.qualificationRate}
                suffix="%"
                color="#8B5CF6"
                description="Answered all questions"
              />
              <StatCard
                icon={CalendarCheck}
                label="Meetings Booked"
                value={funnel.meetingsBooked}
                color="#D4AF37"
                description="Calendar events created"
              />
              <StatCard
                icon={TrendingUp}
                label="Booking Rate"
                value={funnel.bookingRate}
                suffix="%"
                color="#10B981"
                description="Meetings / Total leads"
              />
            </div>
          </div>

          {/* ── 4. Speed & Efficiency ───────────────────────────── */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 md:p-6 mb-8">
            <SectionHeading
              icon={Timer}
              title="Speed & Efficiency"
              subtitle='The "Zero-Minute" metrics — your AI advantage over human managers'
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard
                icon={Zap}
                label="Avg Response Time"
                value={speed.avgResponseTime}
                color="#10B981"
                description="Autonomous response"
              />
              <StatCard
                icon={Bot}
                label="AI Autonomy Rate"
                value={speed.aiAutonomyRate}
                suffix="%"
                color="#D4AF37"
                description="No human intervention"
              />
              <StatCard
                icon={Clock}
                label="Manual Hours Saved"
                value={speed.manualHoursSaved}
                suffix="h"
                color="#8B5CF6"
                description="msgs × 2 min estimate"
              />
            </div>
          </div>

          {/* ── 5. Lead Quality Insights ────────────────────────── */}
          {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 md:p-6">
              <SectionHeading
                icon={DollarSign}
                title="Budget Distribution"
                subtitle="Lead segmentation by budget range"
              />
              <div className="h-[250px] mt-2">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={MOCK_BUDGET_DISTRIBUTION} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis
                      dataKey="range"
                      tick={{ fill: "#9CA3AF", fontSize: 11 }}
                      axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: "#9CA3AF", fontSize: 11 }}
                      axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#1a1d24",
                        border: "1px solid rgba(255,255,255,0.2)",
                        borderRadius: "8px",
                        color: "#F3F4F6",
                        fontSize: "12px",
                      }}
                      labelStyle={{ color: "#9CA3AF" }}
                    />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                      {MOCK_BUDGET_DISTRIBUTION.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 md:p-6">
              <SectionHeading
                icon={Users}
                title="Buyer Type Ratio"
                subtitle="Investors vs Owner-Occupiers"
              />
              <div className="h-[250px] mt-2 flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={MOCK_BUYER_TYPE}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={4}
                      dataKey="value"
                      stroke="none"
                    >
                      {MOCK_BUYER_TYPE.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltipPie />} />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value) => (
                        <span style={{ color: "#9CA3AF", fontSize: "12px" }}>{value}</span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-8 mt-2">
                {MOCK_BUYER_TYPE.map((entry) => (
                  <div key={entry.name} className="text-center">
                    <div className="font-heading font-bold text-2xl text-[#F3F4F6]">
                      {entry.value}%
                    </div>
                    <div className="text-[#9CA3AF] text-xs font-body">{entry.name}</div>
                  </div>
                ))}
              </div>
            </div>
          </div> */}

          {/* ── 6. Hot Leads List ───────────────────────────────── */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 md:p-6 mb-8">
            <SectionHeading
              icon={Flame}
              title="Hot Leads"
              subtitle="Leads requesting human handoff or in final booking stage"
            />
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-[#9CA3AF] text-xs font-body uppercase tracking-wider py-3 pr-4">Name</th>
                    <th className="text-[#9CA3AF] text-xs font-body uppercase tracking-wider py-3 pr-4">Contact</th>
                    <th className="text-[#9CA3AF] text-xs font-body uppercase tracking-wider py-3 pr-4">Budget</th>
                    <th className="text-[#9CA3AF] text-xs font-body uppercase tracking-wider py-3 pr-4">Status</th>
                    <th className="text-[#9CA3AF] text-xs font-body uppercase tracking-wider py-3 pr-4">Time</th>
                    <th className="text-[#9CA3AF] text-xs font-body uppercase tracking-wider py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {hotLeads.length > 0 ? (
                    hotLeads.map((lead) => (
                      <tr key={lead.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                        <td className="py-3 pr-4">
                          <span className="text-[#F3F4F6] text-sm font-body font-medium">{lead.name}</span>
                        </td>
                        <td className="py-3 pr-4">
                          <span className="text-[#9CA3AF] text-sm font-mono">{lead.contact}</span>
                        </td>
                        <td className="py-3 pr-4">
                          <span className="text-[#D4AF37] text-sm font-heading font-semibold">{lead.budget}</span>
                        </td>
                        <td className="py-3 pr-4">
                          {lead.status === "Handoff Requested" ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-body bg-amber-500/10 text-amber-400 border border-amber-500/20">
                              <AlertTriangle className="w-3 h-3" />
                              Handoff Requested
                            </span>
                          ) : lead.status === "Meeting Booked" ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-body bg-blue-500/10 text-blue-400 border border-blue-500/20">
                              <CalendarCheck className="w-3 h-3" />
                              Meeting Booked
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-body bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                              <CalendarCheck className="w-3 h-3" />
                              {lead.status}
                            </span>
                          )}
                        </td>
                        <td className="py-3 pr-4">
                          <span className="text-[#9CA3AF] text-xs font-body">{lead.time}</span>
                        </td>
                        <td className="py-3">
                          <button
                            onClick={() => {
                              const route =
                                lead.platform === "telegram"
                                  ? `/dashboard/chat-telegram?id=${lead.id}`
                                  : lead.platform === "ghl"
                                    ? `/dashboard/chat-ghl?id=${lead.id}`
                                    : `/dashboard/chat-whatsapp?id=${lead.id}`;
                              navigate(route);
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-body font-medium bg-[#00D1FF]/10 text-[#00D1FF] border border-[#00D1FF]/20 hover:bg-[#00D1FF]/20 hover:border-[#00D1FF]/40 transition-all duration-200"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Open Chat
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="py-8">
                        <div className="flex flex-col items-center justify-center">
                          <Flame className="w-8 h-8 text-[#9CA3AF]/30 mb-2" />
                          <p className="text-[#9CA3AF]/60 text-sm font-body text-center">No hot leads yet</p>
                          <p className="text-[#9CA3AF]/40 text-xs font-body text-center mt-1">Leads in handoff or booking stages will appear here</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── 7. Property Heatmap ─────────────────────────────── */}
          {/* <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 md:p-6 mb-8">
            <SectionHeading
              icon={Building2}
              title="Most Requested Properties"
              subtitle="Top 5 properties by enquiry volume — consider price or ad adjustments"
            />
            <div className="space-y-3 mt-2">
              {MOCK_TOP_PROPERTIES.map((property, idx) => {
                const maxEnquiries = MOCK_TOP_PROPERTIES[0].enquiries;
                const widthPercent = (property.enquiries / maxEnquiries) * 100;
                return (
                  <div key={property.id} className="group">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-3">
                        <span className="text-[#D4AF37] font-mono text-xs w-5">#{idx + 1}</span>
                        <Home className="w-4 h-4 text-[#9CA3AF] group-hover:text-[#00D1FF] transition-colors" />
                        <span className="text-[#F3F4F6] text-sm font-body">{property.name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[#9CA3AF] text-sm font-mono">{property.enquiries} enquiries</span>
                        <span className="text-emerald-400 text-xs font-body">{property.trend}</span>
                      </div>
                    </div>
                    <div className="h-2 bg-white/5 rounded-full overflow-hidden ml-8">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-[#D4AF37]/60 to-[#D4AF37] transition-all duration-700"
                        style={{ width: `${widthPercent}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div> */}

        </main>
      </div>
    </div>
  );
};

export default Dashboard;
